import {
  useShouldFetchAggregatesAndSegments,
  useShouldFetchDataDumpForGroupTiles,
  useShouldFetchDataDumpSubordinateProcesses,
  useShouldFetchDataDumpsPerProcess,
  useShouldFetchExpandedTilesData,
  useShouldFetchFullRangeDataDump,
  useShouldFetchLastDataDumpRow,
  useShouldFetchLastDataDumpRowPerProcess,
  useShouldFetchLotProgress,
  useShouldFetchMidgardLotData,
  useShouldFetchShifts,
  useShouldFetchCurrentSegment,
  useShouldFetchSupervisorContext,
  useShouldFetchTimelineDataDumpRows,
} from "components/DataGetter/DataGetter";
import {
  useShouldFetchCustomTrendChartDataDumpsForCount,
  useShouldFetchSegments,
  useShouldFetchTrendChartDataDumps,
  useShouldUseDataDumpForHubletView,
  useShouldUseDataDumpsForGroupMetrics,
} from "utils/customHooks/whichServerHooks";
import { SERVER_NAMES } from "../../components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useShouldFetchMidgardOverriddenMetrics } from "components/DataGetter/CustomMetricsAggregatesGetter";
import { useShouldFetchPnlChartLossesByUnit } from "components/Dashboard/PnlSummaryChart/PnlSummaryChartContent/PnlChartColumns/data-getters/PnlChartLossCountGetter";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useCustomerKey } from "./useCustomerKey";
import { useMemo } from "react";

const DATA_ON_ALL_PAGES = [
  SERVER_NAMES.launchDarkly,
  SERVER_NAMES.auth0,
  SERVER_NAMES.midgardCustomerInfo,
];

/** used to display grey/yellow/red/green color in flight debugger depending if the dataset is not-required/leftover/fetching/ fetched respectively
 *
 * this function uses all the same functions as DataGetter uses to fetch these datasets so that it remains 1-to-1 deterministic
 */
export function useDataRequiredOnThisPage() {
  const shouldUseFullRangeDataDump = useShouldFetchFullRangeDataDump();
  const shouldUseDataDumpsPerProcess = useShouldFetchDataDumpsPerProcess();
  const shouldUseDataDumpsForTrendChart = useShouldFetchTrendChartDataDumps();
  const shouldFetchLastDataDumpRowPerProces =
    useShouldFetchLastDataDumpRowPerProcess();
  const shouldUseDataDumpsForGroupMetrics =
    useShouldUseDataDumpsForGroupMetrics();
  const shouldFetchAggregatesAndSegments =
    useShouldFetchAggregatesAndSegments();
  const shouldFetchShifts = useShouldFetchShifts();
  const shouldFetchCurrentSegment = useShouldFetchCurrentSegment();
  const shouldFetchCustomTrendChartDataDumpsForCount =
    useShouldFetchCustomTrendChartDataDumpsForCount();
  const shouldFetchHubletViewDataDump = useShouldUseDataDumpForHubletView();
  const { shouldFetchMidgardOverriddenMetricsDataDumps } =
    useShouldFetchMidgardOverriddenMetrics();
  const shouldFetchLastDataDumpRow = useShouldFetchLastDataDumpRow();
  const shouldFetchPnlChartLossesByUnit = useShouldFetchPnlChartLossesByUnit();
  const shouldFetchLotProgress = useShouldFetchLotProgress();
  const { isUsingRateRollingTimeWindow } = useSettings();
  const shouldFetchDataDumpForGroupTiles =
    useShouldFetchDataDumpForGroupTiles();
  const shouldFetchDataDumpSubordinateProcesses =
    useShouldFetchDataDumpSubordinateProcesses();
  const shouldFetchExpandedTilesData = useShouldFetchExpandedTilesData();
  const shouldFetchMidgardLotData = useShouldFetchMidgardLotData();
  const shouldFetchTimelineDataDumpRows = useShouldFetchTimelineDataDumpRows();
  const shouldFetchSupervisorContext = useShouldFetchSupervisorContext();
  const shouldFetchSegments = useShouldFetchSegments();
  const ckey = useCustomerKey();
  const dataRequired = useMemo(() => {
    const dataRequired: (keyof typeof SERVER_NAMES)[] = [
      ...DATA_ON_ALL_PAGES,
      ...(shouldFetchShifts ? [SERVER_NAMES.askrShifts] : []),
      ...(shouldFetchCurrentSegment ? [SERVER_NAMES.askrCurrentSegment] : []),
      ...(shouldFetchAggregatesAndSegments
        ? [SERVER_NAMES.askrAggregates]
        : []),
      ...(shouldFetchSegments ? [SERVER_NAMES.askrSegments] : []),
      ...(shouldUseFullRangeDataDump
        ? [SERVER_NAMES.midgardDataDumpFullRange]
        : []),
      ...(shouldUseDataDumpsForTrendChart
        ? [SERVER_NAMES.midgardDataDumpsTrendChart]
        : []),
      ...(shouldUseDataDumpsPerProcess
        ? [SERVER_NAMES.midgardDataDumpsFullRangeByProcess]
        : []),
      ...(shouldUseDataDumpsForGroupMetrics
        ? [SERVER_NAMES.midgardDataDumpFullRange]
        : []),
      ...(shouldFetchCustomTrendChartDataDumpsForCount
        ? [SERVER_NAMES.midgardDataDumpsTrendChartCounts]
        : []),
      ...(shouldFetchHubletViewDataDump
        ? [SERVER_NAMES.midgardDataDumpHubletView]
        : []),
      ...(shouldFetchMidgardOverriddenMetricsDataDumps
        ? [SERVER_NAMES.midgardDataDumpOverriddenMetrics]
        : []),
      ...(shouldFetchLastDataDumpRow
        ? [SERVER_NAMES.midgardLastDataDumpRow]
        : []),
      ...(shouldFetchLastDataDumpRowPerProces
        ? [SERVER_NAMES.midgardLastDataDumpRowPerProcess]
        : []),
      ...(shouldFetchPnlChartLossesByUnit
        ? [SERVER_NAMES.midgardDataDumpsPnlLossesByUnit]
        : []),
      ...(shouldFetchLotProgress
        ? [SERVER_NAMES.midgardDataDumpSinceLotStart]
        : []),
      ...(shouldFetchHubletViewDataDump && isUsingRateRollingTimeWindow
        ? [SERVER_NAMES.midgardDataDumpRollingWindow]
        : []),
      ...(shouldFetchDataDumpForGroupTiles
        ? [SERVER_NAMES.midgardDataDumpsGroupTiles]
        : []),
      ...(shouldFetchDataDumpSubordinateProcesses
        ? [SERVER_NAMES.midgardDataDumpFullRangeSubordinateProcesses]
        : []),
      ...(shouldFetchExpandedTilesData ? [SERVER_NAMES.midgardSku] : []),
      ...(shouldFetchMidgardLotData
        ? [SERVER_NAMES.midgardLotInfo, SERVER_NAMES.midgardLotCounts]
        : []),
      ...(shouldFetchTimelineDataDumpRows
        ? [SERVER_NAMES.midgardDataDumpRows]
        : []),
      ...(shouldFetchSupervisorContext
        ? [SERVER_NAMES.supervisorAllocation]
        : []),
      ...(ckey
        ? [SERVER_NAMES.midgardProcessGroups, SERVER_NAMES.midgardWorkSchedules]
        : []),
    ];

    return dataRequired;
  }, [
    shouldFetchShifts,
    shouldFetchCurrentSegment,
    shouldFetchAggregatesAndSegments,
    shouldFetchSegments,
    shouldUseFullRangeDataDump,
    shouldUseDataDumpsForTrendChart,
    shouldUseDataDumpsPerProcess,
    shouldUseDataDumpsForGroupMetrics,
    shouldFetchCustomTrendChartDataDumpsForCount,
    shouldFetchHubletViewDataDump,
    shouldFetchMidgardOverriddenMetricsDataDumps,
    shouldFetchLastDataDumpRow,
    shouldFetchLastDataDumpRowPerProces,
    shouldFetchPnlChartLossesByUnit,
    shouldFetchLotProgress,
    isUsingRateRollingTimeWindow,
    shouldFetchDataDumpForGroupTiles,
    shouldFetchDataDumpSubordinateProcesses,
    shouldFetchExpandedTilesData,
    shouldFetchMidgardLotData,
    shouldFetchTimelineDataDumpRows,
    shouldFetchSupervisorContext,
    ckey,
  ]);
  return dataRequired;
}
